"use client";

import { Input } from "@/components/ui";
import {
	ASSETS_BASE_PREFIX,
	BASE_URL,
	LOGO_PLACEHOLDER_IMAGE_URL,
	RICH_TEXT_MEDIA_PATH,
} from "@/lib/constants";
import { appendTimestampToFilename, formatImageUrl, sanitizeFilename } from "@/lib/utils";
import { useCustomEditor } from "@/providers/custom-editor-provider";
import { TPostMedia } from "@/types/post";
import { Loader2Icon, Trash2Icon, UploadCloudIcon } from "lucide-react";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

const EditorMediaUpload = ({
	onImagesSelected,
	onClose,
}: {
	onImagesSelected: (images: Array<Omit<TPostMedia, "id">>) => void;
	onClose: () => void;
}) => {
	const { images } = useCustomEditor();

	const [isUploading, setIsUploading] = useState<boolean>(false);
	const [selectedImages, setSelectedImages] = useState<Array<Omit<TPostMedia, "id">>>([]);
	const [imageLink] = useState<string>("");

	const uploadImage = useCallback(async (file: File) => {
		try {
			setIsUploading(true);

			const encodedFileName = appendTimestampToFilename(sanitizeFilename(file.name));

			// NOTE: Old object path: `rich-text-editor-media/${encodedFileName}`
			const inputObjectKey = `${RICH_TEXT_MEDIA_PATH}/${encodedFileName}`;

			const url = new URL(`${BASE_URL}/api/presign`);
			url.searchParams.set("key", inputObjectKey);

			const res = await fetch(url);

			if (!res.ok) throw new Error("Failed to get presigned URL");

			const { preSignedUrl, objectPath } = await res.json();

			if (typeof objectPath !== "string") {
				throw new Error("Invalid object path");
			}

			const s3UploadResponse = await fetch(preSignedUrl, {
				method: "PUT",
				body: file,
			});
			if (!s3UploadResponse.ok) throw new Error("Failed to upload image to S3");

			const uploadedImageUrl = `${ASSETS_BASE_PREFIX}/${objectPath}`;
			setSelectedImages((prev) => [
				...prev,
				{
					objectPath: objectPath,
					url: uploadedImageUrl,
					alt: encodedFileName || "Image",
				},
			]);
		} catch (error) {
			console.error("Error uploading image:", error);

			toast.error("Failed to upload image");
		} finally {
			setIsUploading(false);
		}
	}, []);

	const handleEditorMediaUpload = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const files = Array.from(event.target.files || []);
			Promise.all(files.map((file) => uploadImage(file)));
		},
		[uploadImage]
	);

	const handleAddImages = () => {
		if (selectedImages.length > 0) {
			onImagesSelected(selectedImages);
			onClose();
		}
	};

	useEffect(() => {
		if (images.length > 0) {
			setSelectedImages(images);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<div className="py-4">
			<div className="group relative flex cursor-pointer flex-col items-center justify-center overflow-hidden rounded-md border py-4">
				<div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-200">
					<UploadCloudIcon className="h-6 w-6 text-gray-600" />
				</div>

				<p className="mb-1 mt-3 text-sm text-gray-500">
					<span className="text-brand-primary-600 font-semibold">Click to upload</span> or drag and
					drop
				</p>

				<span className="text-xs text-gray-500">PNG, JPG, JPEG or GIF (max. 1400x1400px)</span>

				<Input
					type="file"
					multiple
					className="absolute inset-0 h-full w-full opacity-0"
					accept="image/x-png,image/jpeg,image/gif"
					onChange={handleEditorMediaUpload}
					disabled={isUploading}
				/>
			</div>

			{/* Show selected images preview */}
			{selectedImages.length > 0 && (
				<div className="mt-4 grid grid-cols-2 gap-2">
					{selectedImages.map((img, idx) => (
						<div key={idx} className="relative">
							<Image
								src={formatImageUrl(img.objectPath) || LOGO_PLACEHOLDER_IMAGE_URL}
								alt={img.alt || `Image ${idx + 1}`}
								className="h-24 w-full rounded object-cover"
								width={300}
								height={200}
							/>

							<button
								onClick={() => setSelectedImages((prev) => prev.filter((_, i) => i !== idx))}
								className="absolute right-1 top-1 rounded-full bg-gray-100 p-1 text-gray-700"
							>
								<Trash2Icon className="h-4 w-4" />
							</button>
						</div>
					))}
				</div>
			)}

			<button
				onClick={handleAddImages}
				className="mt-4 block w-full rounded-md bg-brand-primary py-2 text-sm text-white disabled:cursor-not-allowed disabled:opacity-50"
				disabled={isUploading || (selectedImages.length === 0 && !imageLink)}
			>
				{isUploading ? (
					<Loader2Icon className="mx-auto h-6 w-6 animate-spin stroke-white" />
				) : (
					`Add Image${selectedImages.length > 1 ? "s" : ""}`
				)}
			</button>
		</div>
	);
};

export default EditorMediaUpload;
